// Everything the app remembers: things bought before (offered as suggestions) and sections people
// chose. Search, change the section, or forget.
import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { isSuggestable, orderedCategories, words } from '@milk/shared';
import { linkProps } from '../lib/router';
import { forgetMemory, guessCategory, updateMemory, useStore } from '../store/store';
import { Empty, useToast } from '../components/ui';

export function RememberedScreen() {
  const memories = useStore((s) => s.memories);
  const categories = useStore((s) => s.categories);
  const [q, setQ] = useState('');
  const toast = useToast();
  const cats = orderedCategories(null, Object.values(categories));
  const list = useMemo(() => {
    const needle = words(q).join(' ');
    return Object.values(memories)
      .filter((m) => !needle || words(m.name).join(' ').includes(needle))
      .sort((a, b) => b.timesBought - a.timesBought || a.name.localeCompare(b.name));
  }, [memories, q]);

  return (
    <>
      <div className="page-head">
        <a className="icon-btn" {...linkProps('/settings')} aria-label="Back to settings"><ArrowLeft size={22} aria-hidden /></a>
        <h1>Remembered items</h1>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Things are remembered once they’re bought (checked off). Typos and things removed without being bought are not.
        Forget anything you don’t want suggested.
      </p>
      <div className="card">
        <label htmlFor="mem-q" className="sr-only">Search remembered items</label>
        <input id="mem-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search remembered items" />
        {list.length === 0 ? <Empty title={q ? 'Nothing matches' : 'Nothing remembered yet'}><p>Check items off as you shop and they’ll show up here.</p></Empty> : (
          <ul className="list-rows" style={{ marginTop: 8 }} aria-label="Remembered items">
            {list.map((m) => {
              const sel = m.categoryId ?? '';
              const auto = categories[guessCategory(m.name)];
              return (
                <li key={m.key}>
                  <span className="grow">
                    <strong>{m.name}</strong>
                    <span className="muted small" style={{ display: 'block' }}>
                      {m.timesBought ? `Bought ${m.timesBought} time${m.timesBought > 1 ? 's' : ''}` : 'Section choice only'}
                      {!isSuggestable(m) && m.timesBought > 0 ? ' · not suggested any more' : ''}
                    </span>
                  </span>
                  <label className="sr-only" htmlFor={`mc-${m.key}`}>Section for {m.name}</label>
                  <select id={`mc-${m.key}`} style={{ width: 'auto', maxWidth: '45%' }} value={sel}
                    onChange={(e) => updateMemory(m.key, { categoryId: e.target.value || null })}>
                    <option value="">Automatic{auto && !m.categoryId ? ` (${auto.name})` : ''}</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                  </select>
                  <button className="btn ghost danger" onClick={() => { forgetMemory(m.key); toast(`Forgot ${m.name}`); }} aria-label={`Forget ${m.name}`}>Forget</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
